import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, Pencil, Trash2, Users } from "lucide-react";
import { clientService, Client } from "../services/client.service";
import CreateClientModal from "../components/CreateClientModal";
import EditClientModal from "../components/EditClientModal";
import DeleteClientModal from "../components/DeleteClientModal";
import "../styles/clients.css";

export default function ClientsListPage() {
    const navigate = useNavigate();
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [showCreate, setShowCreate] = useState(false);
    const [editClient, setEditClient] = useState<Client | null>(null);
    const [deleteClient, setDeleteClient] = useState<Client | null>(null);

    const fetchClients = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await clientService.getAll();
            setClients(data);
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to load clients.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const handleCreated = () => {
        setShowCreate(false);
        fetchClients();
    };

    const handleUpdated = () => {
        setEditClient(null);
        fetchClients();
    };

    const handleDeleted = () => {
        setDeleteClient(null);
        fetchClients();
    };

    const formatDate = (iso: string) =>
        new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });

    return (
        <>
            <div className="clients-container">
                <div className="clients-header">
                    <h1>Clients</h1>
                    <div className="clients-header-actions">
                        <button className="btn-create" onClick={() => setShowCreate(true)}>
                            <Plus size={16} />
                            <span>Create Client</span>
                        </button>
                    </div>
                </div>

                {error && <div className="modal-error" style={{ marginBottom: 16 }}>{error}</div>}

                {loading ? (
                    <div className="clients-loading">
                        <div className="auth-spinner" />
                    </div>
                ) : clients.length === 0 ? (
                    <div className="clients-empty">
                        <div className="clients-empty-icon">
                            <Users size={24} />
                        </div>
                        <h3>No clients yet</h3>
                        <p>Create your first client to get started.</p>
                        <button className="btn-create" onClick={() => setShowCreate(true)}>
                            <Plus size={16} />
                            <span>Create Client</span>
                        </button>
                    </div>
                ) : (
                    <div className="clients-table-card">
                        <table className="clients-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Created</th>
                                    <th style={{ width: 120 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clients.map((client) => (
                                    <tr key={client.id}>
                                        <td className="client-name">{client.name}</td>
                                        <td>
                                            {client.type ? (
                                                <span className="client-type-badge">{client.type}</span>
                                            ) : (
                                                <span className="client-type-badge empty">—</span>
                                            )}
                                        </td>
                                        <td>{formatDate(client.createdAt)}</td>
                                        <td>
                                            <div className="client-actions">
                                                <button
                                                    className="btn-action"
                                                    title="View"
                                                    onClick={() => navigate(`/clients/${client.id}`)}
                                                >
                                                    <Eye size={14} />
                                                </button>
                                                <button
                                                    className="btn-action"
                                                    title="Edit"
                                                    onClick={() => setEditClient(client)}
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    className="btn-action danger"
                                                    title="Delete"
                                                    onClick={() => setDeleteClient(client)}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showCreate && (
                <CreateClientModal
                    onClose={() => setShowCreate(false)}
                    onCreated={handleCreated}
                />
            )}
            {editClient && (
                <EditClientModal
                    client={editClient}
                    onClose={() => setEditClient(null)}
                    onUpdated={handleUpdated}
                />
            )}
            {deleteClient && (
                <DeleteClientModal
                    client={deleteClient}
                    onClose={() => setDeleteClient(null)}
                    onDeleted={handleDeleted}
                />
            )}
        </>
    );
}
